import React, { useEffect, useState } from 'react';
import CusPageCardContainer from '../../../components/shared/CusPageCardContainer';
import { Stack } from '@mui/material';
import { ENUM_SCHEDULER_STATUS, ENUM_SITE_ID, TEST_MODE } from '../../../enums/enums';
import { useTranslation } from 'react-i18next';
import SchedulerFields from './SchedulerFields';
import MailingItemPayload from './MailingItemPayload';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { API_MAILING } from '../../../api/mailing.js';
import useToast from '../../../hooks/useToast.js';
import ShowContentWitchEmpty from '../../../components/material-ui/show_content_empty/ShowContentWitchEmpty.jsx';
import LoaderContainer from '../../../components/material-ui/loader_container/LoaderContainer.jsx';
import { getReadOnlyScheduler } from '../../../utils.js';

export default function MailingItem() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const pageSearch = location.search;
  const cloneId = new URLSearchParams(pageSearch).get('clone');

  const { successToast, infoToast, errorToast } = useToast();
  const [mailingInfo, setMailingInfo] = useState({});
  const [saveFilter, setSaveFilter] = useState(null);
  const [fieldInfo, setFieldInfo] = useState({});
  const [isTestSent, setIsTestSent] = useState(true);
  const [loader, setLoader] = useState(false);
  const [searchDone, setSearchDone] = useState(false);
  const [readOnly, setReadOnly] = useState(false);

  const isCreate = id == 'new';

  const title = isCreate ? 'Create a mailing' : fieldInfo?.title || 'рассылка #' + id;

  const onFindClick = (filter) => {
    if (TEST_MODE) {
      setMailingInfo({ userCount: 105 });
      return;
    }

    if (filter.depositCountFrom || filter.depositCountTo) {
      if (!filter.depositCurrency) {
        errorToast('Please select a currency');
        return;
      }
    }

    const dataFilter = {};

    for (let name in filter) {
      let value = filter[name];

      if (value === 'skip') {
        dataFilter[name] = null;
        continue;
      }

      if (typeof value === 'string' && !isNaN(value) && value.trim() !== '') {
        dataFilter[name] = Number(value);
      } else {
        dataFilter[name] = value;
      }
    }

    API_MAILING.filter(dataFilter)
      .then((e) => {
        const userCount = e.data.userCount;
        setMailingInfo({ userCount: userCount, ...dataFilter });
        setSearchDone(true);
      })
      .catch(console.error);
  };

  const onSendMailing = (data) => {
    const getError = (newData) => {
      if (!newData.title) return 'Please provide the name of the mailing';
      if (newData.isRepeatMailing) {
        if (!newData.sendDateFrom) return 'Please fill the initial date of dispatch';
        if (!newData.sendDateTo) return 'Please indicate the final date of dispatch';
        if (!newData.sendAtTime) return 'Please indicate the dispatch time';
        if (!newData.sendByDays?.filter?.((el) => el)?.length) {
          return 'Please select at least one day';
        }
      } else {
        if (!newData.sendAtDate) return 'Please fill in the date and time of sending';
      }

      if (!newData.messageList?.en || newData.messageList?.en == '<p></p>') {
        return 'Please complete the message in English.';
      }
    };

    const newData = { ...mailingInfo, ...data };
    const error = getError(newData);

    if (error) {
      errorToast(t(error));
      return;
    }

    if (!isTestSent) {
      const infoText =
        'Before you save the newsletter, you need to send this message to yourself and make sure that the message is formatted correctly.';

      infoToast(t(infoText));
      return;
    }

    if (newData.isRepeatMailing) {
      newData.sendAtDate = null;
    } else {
      newData.sendDateFrom = null;
      newData.sendDateTo = null;
      newData.sendAtTime = null;
      newData.sendByDays = null;
    }

    delete newData.isRepeatMailing;

    isCreate ? onCreateMailing(newData) : onUpdateMailing(newData);
  };

  const onCreateMailing = (createData) => {
    API_MAILING.create(createData)
      .then((e) => {
        successToast(t('The mailing list has been created successfully.'));
        navigate('/mailing/scheduler');
      })
      .catch((e) => {});
  };

  const onUpdateMailing = (newData) => {
    API_MAILING.update(+id, newData)
      .then((e) => {
        successToast(t('The mailing list has been updated successfully'));
        navigate('/mailing/scheduler');
      })
      .catch(console.error);
  };

  const onTestSendMailing = (testInfo) => {
    console.log('testInfo.messageList?.en', testInfo.messageList?.en);

    // первый он же и единственный
    const firstText = Object.values(testInfo.messageList)?.[0];
    if (!firstText?.length || firstText == '<p></p>') {
      infoToast('Please complete the message.');
      return;
    }

    // if (!testInfo.messageList?.en || testInfo.messageList?.en == '<p></p>') {
    //   infoToast('Please complete the message in English.');
    //   return;
    // }

    API_MAILING.sendTest(testInfo)
      .then(() => {
        successToast(t('Test mailing sent'));
        setIsTestSent(true);
      })
      .catch(console.error);
  };

  useEffect(() => {
    if (!id) return;
    if (id == 'new') {
      if (!cloneId) return;
    }

    const isClone = id == 'new' && cloneId;

    const mailingId = +id || +cloneId;
    setLoader(true);
    API_MAILING.get(mailingId)
      .then((e) => {
        const data = e.data.data;
        const newData = { ...data };
        const title = isClone ? `clone ${newData?.title}` : newData?.title;

        if (!newData.depositCountTo) newData.depositCountTo = 'skip';
        if (!newData.depositCountFrom) newData.depositCountFrom = 'skip';
        if (!newData.depositCurrency) newData.depositCurrency = 'skip';
        if (!newData.unreadMsg) newData.unreadMsg = 'skip';
        if (!newData.pressBotKey) newData.pressBotKey = 'skip';
        if (!newData.depositIsLocked) newData.depositIsLocked = 'skip';
        if (!newData.runGame) newData.runGame = 'skip';
        if (!newData.payoutInvoice) newData.payoutInvoice = 'skip';
        if (!newData.regType) newData.regType = 'skip';
        if (!newData.botDeleted) newData.botDeleted = 'skip';
        if (!newData.userStatus) newData.userStatus = 'skip';
        if (!newData.phoneNumber) newData.phoneNumber = 'skip';

        setMailingInfo(newData);
        setFieldInfo({
          title: title,
          sendAtDate: newData?.sendAtDate,
          sendDateFrom: newData?.sendDateFrom,
          sendDateTo: newData?.sendDateTo,
          sendAtTime: newData?.sendAtTime,
          sendByDays: newData?.sendByDays,
          messageList: newData?.messageList || {},
          isRepeatMailing: !!newData?.sendDateFrom
        });

        const saveFilter = { ...newData };
        const readOnly = getReadOnlyScheduler(saveFilter.status);

        delete saveFilter.messageList;
        delete saveFilter.title;
        delete saveFilter.sendAtDate;
        delete saveFilter.sendDateFrom;
        delete saveFilter.sendDateTo;
        delete saveFilter.sendAtTime;
        delete saveFilter.sendByDays;
        delete saveFilter.status;

        setSaveFilter(saveFilter);
        setReadOnly(readOnly);

        if (isClone) onFindClick(saveFilter);
      })
      .catch(console.error)
      .finally(() => setLoader(false));
  }, [id, cloneId]);

  return (
    <CusPageCardContainer title={title} backInfo={{ text: 'Back', to: '/mailing/scheduler' }}>
      <LoaderContainer loader={loader} isBox>
        <Stack id={ENUM_SITE_ID.schedulerCreateMailing} gap={2}>
          <MailingItemPayload
            saveFilter={saveFilter}
            readOnly={readOnly}
            onFindClick={onFindClick}
            setSearchDone={setSearchDone}
          />
          <ShowContentWitchEmpty
            condition={mailingInfo.userCount || readOnly}
            conditionPlug={mailingInfo.userCount === 0}
            plugText="No users found, please change filter">
            <SchedulerFields
              userCount={mailingInfo.userCount}
              groupId={mailingInfo.groupId}
              isCreate={isCreate}
              info={fieldInfo}
              searchDone={searchDone}
              readOnly={readOnly}
              onSendMailing={onSendMailing}
              onTestSendMailing={onTestSendMailing}
              setIsTestSent={setIsTestSent}
            />
          </ShowContentWitchEmpty>
        </Stack>
      </LoaderContainer>
    </CusPageCardContainer>
  );
}
